<template>
  <b-card no-body>
    <b-tabs
      :value="activeTabIndex"
      @input="onChangeTab"
    >
      <!--Transactions-->
      <b-tab
        :title="$tk('ClientPortal.Account.Transactions')"
      >
        <b-row>
          <b-col
            sm="4"
          >
            <b-button
              v-if="wrapper.canManageDeposits"
              variant="primary"
              class="mb-1 ml-1"
              :to="'balance/deposit'"
              size="sm"
            >
              <GjIcon
                class="mr-50"
                name="Plus"
              />
              {{ $tk('ClientPortal.Account.Deposit') }}
            </b-button>
          </b-col>
          <b-col
            class="d-flex justify-content-end align-items-baseline"
            sm="8"
          >
            <span class="mr-50">{{ $tk('ClientPortal.MasterTable.ShowDataPer') }}</span>
            <b-dropdown
              ref="depositDateRange"
              menu-class="py-0"
              variant="outline-dark"
              class="bg-white pr-1 mb-1"
              no-caret
              size="sm"
              right
            >
              <template #button-content>
                {{ wrapper.depositDateRange }}
                <GjIcon
                  name="Calendar"
                  class="ml-50"
                />
              </template>
              <div class="p-2">
                <date-range-picker
                  ref="depositDateRangePicker"
                  :ranges="predefinedRanges"
                  :allow-compare="false"
                  default-range-select="defaultRange"
                  @submit="submitDepositDateRange"
                  @cancel="closeDepositDateRangePicker"
                />
              </div>
            </b-dropdown>
          </b-col>
        </b-row>
        <b-skeleton-table
          v-if="loading"
          :rows="10"
          :columns="5"
          :table-props="{ bordered: true, striped: true }"
        />
        <template v-else>
          <div class="client-table--account-balance">
            <b-table
              :items="wrapper.transactionModelList"
              :tbody-tr-class="newRecordRowStyle"
              :fields="transationFields"
              class="client-table__data transaction-table"
              show-empty
              :empty-text="$tk('ClientPortal.Account.ThereAreNoTransactionsYet')"
              :current-page="transactionsCurrentPage"
              :per-page="perPage"
              responsive
            >
              <template #cell(transactionStatus)="data">
                <span :class="getCustomClass(data.item.transactionStatus)">{{ data.item.transactionStatus }}</span>
              </template>
              <template #cell(amount)="data">
                <GjIcon
                  :class="getTransactionClass(data.item.type)"
                  :name="getTransactionIcon(data.item.type)"
                /> {{ data.item.amount | formatCurrency }}
              </template>
              <template #empty="scope">
                <div class="text-center p-3 client-table__empty">
                  <div class="empty__client-table">
                    <GjIcon
                      size="64px"
                      name="Payment"
                    />
                    <div class="mb-50">
                      {{ scope.emptyText }}
                    </div>
                    <b-link :to="'balance/deposit'">
                      <GjIcon
                        size="14"
                        name="Plus"
                      />
                      <span class="create-text">{{ $tk('ClientPortal.Account.Deposit') }}</span>
                    </b-link>
                  </div>
                </div>
              </template>
            </b-table>
            <div
              v-if="wrapper.transactionModelList.length > 0"
              class="d-flex border-top p-1 client-table__pagination--balance"
            >
              <b-pagination
                v-model="transactionsCurrentPage"
                :total-rows="transactionsTotalRows"
                :per-page="perPage"
                size="sm"
                class="my-0 mr-1"
                hide-goto-end-buttons
              >
                <template #prev-text>
                  <GjIcon
                    class="mr-25"
                    name="ArrowLeft"
                  />
                </template>
                <template #next-text>
                  <GjIcon
                    class="ml-25"
                    name="ArrowRight"
                  />
                </template>
              </b-pagination>
              <span>
                {{ $tk('ClientPortal.Pagination.Viewing') }} {{ transactionsFromCount }}
                {{ $tk('ClientPortal.Pagination.To') }} {{ transactionsToCount }}
                {{ $tk('ClientPortal.Pagination.Of') }} {{ transactionsTotalRows }}
                {{ $tk('ClientPortal.Pagination.Items') }}
              </span>
            </div>
          </div>
        </template>
      </b-tab>
      <b-tab
        :title="$tk('ClientPortal.Account.WithdrawRequests')"
      >
        <b-row>
          <b-col sm="4">
            <b-button
              v-if="wrapper.withdrawableAmount > 0 && wrapper.canManageDeposits"
              variant="primary"
              class="mb-1 ml-1"
              :to="'balance/withdraw'"
              size="sm"
            >
              <GjIcon
                class="mr-50"
                name="Minus"
              />
              {{ $tk('ClientPortal.Account.Withdraw') }}
            </b-button>
          </b-col>
          <b-col
            class="d-flex justify-content-end align-items-baseline"
            sm="8"
          >
            <span class="mr-50">{{ $tk('ClientPortal.MasterTable.ShowDataPer') }}</span>
            <b-dropdown
              ref="withdrawDateRange"
              menu-class="py-0"
              variant="outline-dark"
              class="bg-white pr-1 mb-1"
              no-caret
              size="sm"
              right
            >
              <template #button-content>
                {{ wrapper.withdrawDateRange }}
                <GjIcon
                  name="Calendar"
                  class="ml-50"
                />
              </template>
              <div class="p-2">
                <date-range-picker
                  ref="withdrawDateRangePicker"
                  :ranges="predefinedRanges"
                  :allow-compare="false"
                  default-range-select="defaultRange"
                  @submit="submitWithdrawDateRange"
                  @cancel="closeWithdrawDateRangePicker"
                />
              </div>
            </b-dropdown>
          </b-col>
        </b-row>
        <!--Withdraw requests-->
        <b-skeleton-table
          v-if="loading"
          :rows="10"
          :columns="5"
          :table-props="{ bordered: true, striped: true }"
        />
        <template v-else>
          <div class="client-table--account-balance">
            <b-table
              :items="wrapper.withdrawRequests"
              :tbody-tr-class="newRecordRowStyle"
              :fields="fields"
              class="client-table__data withdraws-table"
              show-empty
              :empty-text="$tk('ClientPortal.Account.NoWithdrawRequestsHaveBeenMadeYet')"
              :current-page="currentPage"
              :per-page="perPage"
              responsive
            >
              <template #cell(type)>
                <span>
                  {{ $tk('ClientPortal.Account.WithdrawRequest') }}
                </span>
              </template>
              <template #cell(status)="data">
                <span :class="getCustomClass(data.item.status)">{{ data.item.status }}</span>
              </template>
              <template #cell(amount)="data">
                {{ data.item.amount | formatCurrency }}
              </template>
              <template #empty="scope">
                <div class="text-center p-3 client-table__empty">
                  <div class="empty__client-table">
                    <GjIcon
                      size="64px"
                      name="Payment"
                    />
                    <div class="mb-50">
                      {{ scope.emptyText }}
                    </div>
                    <b-link
                      v-if="wrapper.withdrawableAmount > 0 && wrapper.canManageDeposits"
                      :to="'balance/withdraw'"
                    >
                      <GjIcon
                        size="14"
                        name="Minus"
                      />
                      <span class="create-text">{{ $tk('ClientPortal.Account.Withdraw') }}</span>
                    </b-link>
                  </div>
                </div>
              </template>
            </b-table>
            <div
              v-if="wrapper.withdrawRequests.length > 0"
              class="d-flex border-top p-1 account-table__pagination--balance"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                size="sm"
                class="my-0 mr-1"
                hide-goto-end-buttons
              >
                <template #prev-text>
                  <GjIcon
                    class="mr-25"
                    name="ArrowLeft"
                  />
                </template>
                <template #next-text>
                  <GjIcon
                    class="ml-25"
                    name="ArrowRight"
                  />
                </template>
              </b-pagination>
              <span>
                {{ $tk('ClientPortal.Pagination.Viewing') }} {{ fromCount }}
                {{ $tk('ClientPortal.Pagination.To') }} {{ toCount }}
                {{ $tk('ClientPortal.Pagination.Of') }} {{ totalRows }}
                {{ $tk('ClientPortal.Pagination.Items') }}
              </span>
            </div>
          </div>
        </template>
      </b-tab>
      <template #tabs-end>
        <li
          role="presentation"
          class="nav-item align-self-center ml-auto px-2"
        >
          {{ $tk('ClientPortal.Account.Balance') }}
          <span
            v-if="!loading"
            class="font-weight-bold text-dark"
          >
            {{ wrapper.balance | formatCurrency }}
          </span>
        </li>
      </template>
    </b-tabs>
  </b-card>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import DateRangePicker from '@/components/VueDateRangePicker'
import moment from 'moment'
import AccountsService from '@/api/client/accounts.service'
import notification from '@/mixins/toastification'
import { computed, ref } from '@vue/composition-api'
import store from '@/store'

export default {
  components: {
    GjIcon,
    DateRangePicker,
  },
  mixins: [notification],
  data() {
    return {
      wrapper: {},
      loading: true,
      predefinedRanges: {
        today: {
          label: this.$tk('ClientPortal.PredefinedRanges.Today'),
          startDate: moment(),
          endDate: moment(),
        },
        yesterday: {
          label: this.$tk('ClientPortal.PredefinedRanges.Yesterday'),
          startDate: moment().subtract(1, 'days'),
          endDate: moment().subtract(1, 'days'),
        },
        last7Days: {
          label: this.$tk('ClientPortal.PredefinedRanges.Last7Days'),
          startDate: moment().subtract(7, 'days'),
          endDate: moment().subtract(1, 'days'),
        },
        last30Days: {
          label: this.$tk('ClientPortal.PredefinedRanges.Last30Days'),
          startDate: moment().subtract(30, 'days'),
          endDate: moment().subtract(1, 'days'),
        },
        lastMonth: {
          label: this.$tk('ClientPortal.PredefinedRanges.LastMonth'),
          startDate: moment().subtract(1, 'months').startOf('month'),
          endDate: moment().subtract(1, 'month').endOf('month'),
        },
        defaultRange: {
          label: this.$tk('ClientPortal.PredefinedRanges.DefaultRange'),
          startDate: moment().subtract(7, 'days'),
          endDate: moment(),
        },
      },
      totalRows: 10,
      currentPage: 1,
      perPage: 10,
      transactionsTotalRows: 10,
      transactionsCurrentPage: 1,
      tabHashes: ['#transactions', '#withdraw-requests'],
    }
  },
  computed: {
    fromCount() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    accountId() {
      return this.$route.params.accountId
    },
    toCount() {
      const toCount = this.fromCount + this.perPage - 1
      return Math.min(toCount, this.totalRows)
    },
    transactionsFromCount() {
      return (this.transactionsCurrentPage - 1) * this.perPage + 1
    },
    transactionsToCount() {
      const toCount = this.transactionsFromCount + this.perPage - 1
      return Math.min(toCount, this.transactionsTotalRows)
    },
    activeTabIndex() {
      const route = this.$route.hash.toLowerCase()
      return this.tabHashes.findIndex(x => x === route) || 0
    },
    fields() {
      return [
        'type',
        {
          key: 'message',
          label: this.$tk('ClientPortal.Account.Description'),
        },
        'status',
        {
          key: 'date',
          label: this.$tk('ClientPortal.Account.CreatedDate'),
        },
        {
          key: 'amount',
          label: this.$tk('ClientPortal.Account.Transaction'),
        },
      ]
    },
    transationFields() {
      return [
        {
          key: 'transactionType',
          label: this.$tk('ClientPortal.Account.Type'),
        },
        {
          key: 'message',
          label: this.$tk('ClientPortal.Account.Description'),
        },
        {
          key: 'transactionStatus',
          label: this.$tk('ClientPortal.Account.Status'),
        },
        'date',
        {
          key: 'amount',
          label: this.$tk('ClientPortal.Account.Transaction'),
        },
      ]
    },
  },
  watch: {
    accountId() {
      this.getBalanceData(true)
    },
  },
  async mounted() {
    await this.getBalanceData()
  },
  methods: {
    async getBalanceData(accountChanged) {
      if (accountChanged) {
        this.wrapper = {}
        this.$refs.depositDateRangePicker.selectRange('defaultRange')
        this.$refs.withdrawDateRangePicker.selectRange('defaultRange')
      }
      this.loading = true
      try {
        const response = await AccountsService.getBalance(this.wrapper.depositDateRange, this.wrapper.withdrawDateRange, this.$route.params.accountId)
        this.wrapper = response
      } catch (error) {
        this.error(this.$tk('ClientPortal.Account.Error.FaildToLoadData'))
      } finally {
        this.totalRows = this.wrapper.withdrawRequests.length
        this.transactionsTotalRows = this.wrapper.transactionModelList.length
        this.loading = false
      }
    },
    getCustomClass(status) {
      const classOptions = {
        Pending: 'text-warning',
        Completed: 'text-success',
        Rejected: 'text-danger',
        Paid: 'text-primary',
        default: 'text-secondary',
      }
      return classOptions[status] || classOptions.default
    },
    getTransactionIcon(type) {
      if (type === 1 || type === 3) return 'ArrowUpAlt'
      return 'ArrowDownAlt'
    },
    getTransactionClass(type) {
      if (type === 1 || type === 3) return 'text-success'
      return 'text-danger'
    },
    formatDateRange(range) {
      const startDate = range.startDate.format('DD/MM/YYYY')
      const endDate = range.endDate.format('DD/MM/YYYY')
      return `${startDate} - ${endDate}`
    },
    closeDepositDateRangePicker() {
      this.$refs.depositDateRange.hide(true)
    },
    submitDepositDateRange(range) {
      this.wrapper.depositDateRange = this.formatDateRange(range)
      this.closeDepositDateRangePicker()
      this.getBalanceData()
    },
    closeWithdrawDateRangePicker() {
      this.$refs.withdrawDateRange.hide(true)
    },
    submitWithdrawDateRange(range) {
      this.wrapper.withdrawDateRange = this.formatDateRange(range)
      this.closeWithdrawDateRangePicker()
      this.getBalanceData()
    },
    onChangeTab(tab) {
      store.commit('app/TOGGLE_TABLE', tab)
      const hash = this.tabHashes[tab]
      this.$router.replace({ hash })
    },
  },
  setup() {
    const isCreatedNew = computed(() => store.state.app.isCreatedNew)
    const typeOfTable = computed(() => store.state.app.typeOfTable)
    const alreadySet = ref(false)
    const firstRow = ref(null)

    const newRecordRowStyle = () => {
      if (!alreadySet.value) {
        if (typeOfTable.value === 0) {
          firstRow.value = Array.from(document.querySelectorAll('.transaction-table > table > tbody > tr:first-child')).pop()
        } else if (typeOfTable.value === 1) {
          firstRow.value = Array.from(document.querySelectorAll('.withdraws-table > table > tbody > tr:first-child')).pop()
        }
        if (firstRow.value) {
          if (isCreatedNew.value) {
            firstRow.value.classList.add('table-row-animation')
          } else if (firstRow.value.classList.contains('table-row-animation')) {
            firstRow.value.classList.remove('table-row-animation')
          }
          alreadySet.value = true
        }
      }
      return ''
    }
    if (isCreatedNew.value) {
      setTimeout(() => {
        store.commit('app/TOGGLE_NEW_CREATE_STATE', false)
        firstRow.value.classList.remove('table-row-animation')
      }, 3000)
    }
    return {
      isCreatedNew,
      newRecordRowStyle,
    }
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/views/client/account/balance/balance.scss';
@import '@/assets/scss/views/added_row_animation.scss';
</style>
